import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Building2, Eye, EyeOff, Loader2, Shield, ArrowLeft } from 'lucide-react';

// Google Icon component
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Microsoft Icon component
const MicrosoftIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#F25022" d="M1 1h10v10H1z"/>
    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
    <path fill="#FFB900" d="M13 13h10v10H13z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, verifyMFA } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  // MFA State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  
  // SSO State
  const [ssoProviders, setSsoProviders] = useState([]);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  // Fetch SSO providers on mount
  useEffect(() => {
    const fetchSSOProviders = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sso/providers`);
        const data = await response.json();
        setSsoProviders(data.providers || []);
        setSsoEnabled(data.sso_enabled || false);
      } catch (error) {
        console.log('SSO providers not available');
        setSsoEnabled(false);
      }
    };
    fetchSSOProviders();
  }, []);

  // SSO Login handlers
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSSO = () => {
    setSsoLoading('google');
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftSSO = () => {
    toast.info('Microsoft SSO requires Azure AD configuration. Contact your administrator.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      // Check if MFA is required
      if (result.mfa_required) {
        setMfaRequired(true);
        setMfaToken(result.mfa_token);
        toast.info('Please enter your verification code');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    
    if (mfaCode.length !== 6 && mfaCode.length !== 9) {
      toast.error('Please enter a valid code');
      return;
    }
    
    setIsLoading(true);
    try {
      await verifyMFA(mfaToken, mfaCode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaToken('');
    setMfaCode('');
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#111565] rounded-lg mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#111565] tracking-tight">FacilityPro</h1>
          <p className="text-muted-foreground mt-1">Facility Management System</p>
        </div>

        <Card className="border-border/60 shadow-lg">
          {!mfaRequired ? (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-center">Sign in</CardTitle>
                <CardDescription className="text-center">
                  Enter your credentials to access your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-focus"
                      data-testid="login-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className="input-focus pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary touch-target"
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </form>

                {/* SSO Options - Only show if enabled by admin */}
                {ssoEnabled && ssoProviders.length > 0 && (
                  <div className="mt-6">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                      </div>
                    </div>

                    <div className={`mt-4 grid gap-3 ${ssoProviders.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {ssoProviders.find(p => p.id === 'google') && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGoogleSSO}
                          disabled={ssoLoading !== null}
                          className="w-full"
                          data-testid="google-sso-btn"
                        >
                          {ssoLoading === 'google' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <GoogleIcon />
                              <span className="ml-2">Google</span>
                            </>
                          )}
                        </Button>
                      )}
                      {ssoProviders.find(p => p.id === 'microsoft') && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleMicrosoftSSO}
                          disabled={ssoLoading !== null}
                          className="w-full"
                          data-testid="microsoft-sso-btn"
                        >
                          {ssoLoading === 'microsoft' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <MicrosoftIcon />
                              <span className="ml-2">Microsoft</span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">Do not have an account? </span>
                  <Link
                    to="/register"
                    className="text-[#ff6600] hover:text-[#e65c00] font-medium"
                    data-testid="register-link"
                  >
                    Create account
                  </Link>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1">
                <div className="flex items-center justify-center mb-2">
                  <div className="w-12 h-12 rounded-full bg-[#111565]/10 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-[#111565]" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-center">Two-Factor Authentication</CardTitle>
                <CardDescription className="text-center">
                  Enter the 6-digit code from your authenticator app, or use a backup code
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleMFASubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code">Verification Code</Label>
                    <Input
                      id="mfa-code"
                      type="text"
                      placeholder="000000"
                      maxLength={9}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                      className="text-center text-2xl tracking-widest font-mono"
                      autoFocus
                      data-testid="mfa-code-input"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      Enter 6-digit code or backup code (XXXX-XXXX)
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary touch-target"
                    disabled={isLoading || (mfaCode.length !== 6 && mfaCode.length !== 9)}
                    data-testid="mfa-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify'
                    )}
                  </Button>
                </form>

                <div className="mt-4">
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    onClick={handleBackToLogin}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to login
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          FacilityPro - Professional Facility Management
        </p>
      </div>
    </div>
  );
};

export default Login;
